import { Component, inject, input, output, computed } from '@angular/core';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzIconModule } from 'ng-zorro-antd/icon';

import { Location } from '../../../models/classes/location';
import { I18nService } from '../../../services/i18n.service';
import { UiService } from '../../../services/ui.service';
import { CardStatus } from '../../../enums/global.enum';
import { GLOBAL_CONSTANT } from '../../../constants/global.constant';

@Component({
  selector: 'app-status-card',
  imports: [NzCardModule, NzIconModule],
  templateUrl: './status-card.html',
  styleUrls: ['./status-card.scss'],
})
export class StatusCardComponent {
  plant = input<Location | null>(null);
  parentArray = input<Location[]>([]);
  fine = input<boolean>(false);
  modal = input<boolean>(false);
  detail = input<boolean>(false);
  closeBtn = input<boolean>(false);
  renameBtn = input<boolean>(false);
  isEditable = input<boolean>(false);
  editObject = input<any>(null);

  // Status & message for the card banner
  status = input<CardStatus | string>(CardStatus.None);
  message = input<string | null>(null);

  closeEvent = output<void>();
  nav = output<void>();
  delete = output<Location>();
  nameError = output<boolean>();

  public i18n = inject(I18nService);
  private ui = inject(UiService);

  readonly defaultImage = 'assets/images/plant_default_picture.png';

  isMobile = computed<boolean>(() => this.ui.IsMobile());
  isTablet = computed<boolean>(() => this.ui.IsTablet());
  isDesktop = computed<boolean>(() => this.ui.IsDesktop());

  applyBodyStyle = computed(() =>
    this.normalizedStatus() !== CardStatus.None
      ? { 'background-color': 'white', 'border-radius': '0 0 15px 15px' }
      : {}
  );

  // Normalize status to CardStatus enum strings
  normalizedStatus = computed<CardStatus>(() => {
    const raw = (this.status() || CardStatus.None).toString().toLowerCase();
    switch (raw) {
      case 'alert':
        return CardStatus.Alert;
      case 'warning':
        return CardStatus.Warning;
      case 'ok':
        return CardStatus.OK;
      default:
        return CardStatus.None;
    }
  });

  // Sensor count aggregated from the plant
  sensorCount = computed<number>(() => {
    const p = this.plant();
    if (!p) return GLOBAL_CONSTANT.DEFAULT_INITIALIZER;
    const sensors = (p.Assets ?? []).map((a) => a.sensors ?? []).flat();
    return sensors.length;
  });

  canRename = computed<boolean>(() => this.renameBtn() && this.isEditable());
  canDelete = computed<boolean>(() => !!this.plant() && !this.modal());

  // Banner CSS class based on status
  bannerClass = computed<string>(() => {
    switch (this.normalizedStatus()) {
      case CardStatus.Alert:
        return 'status-alert';
      case CardStatus.Warning:
        return 'status-warning';
      case CardStatus.OK:
        return 'status-ok';
      case CardStatus.None:
      default:
        return 'status-none';
    }
  });

  onNav(): void {
    this.nav.emit();
  }

  onDelete(): void {
    const p = this.plant();
    if (p) this.delete.emit(p);
  }

  onNameError(event: boolean): void {
    this.nameError.emit(event);
  }

  onClose(): void {
    this.closeEvent.emit();
  }
}
